<script lang="ts" setup>
import type { DropdownItem, PopperOptions } from "#ui/types";
import { twMerge, twJoin } from "tailwind-merge";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { type IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { useUI } from "#ui/composables/useUI";
import { toRef } from "vue";
import appConfig from "#build/app.config";
import { dropdown } from "#ui/ui.config";
import { mergeConfig, getNuxtLinkProps } from "#ui/utils";

const config = mergeConfig(
    appConfig.ui.strategy,
    appConfig.ui.dropdown,
    dropdown,
);

export interface ExtendedDropdownItem extends DropdownItem {
    badge?: string;
    active?: boolean;
    trailingIcon?: any;
    icon?: IconDefinition | string | undefined;
}

const emit = defineEmits<{
    (e: "close", v: ExtendedDropdownItem): void;
}>();

const props = withDefaults(
    defineProps<{
        items?: ExtendedDropdownItem[][];
        mode?: "click" | "hover";
        open?: boolean;
        disabled?: boolean;
        popper?: PopperOptions;
        openDelay?: number;
        closeDelay?: number;
        class?: any;
        ui?: any;
    }>(),
    {
        items: () => [],
        mode: "click",
        open: void 0,
        disabled: false,
        popper: () => ({}),
        class: () => "",
        ui: () => ({}),
    },
);

const { ui, attrs } = useUI(
    "dropdown",
    toRef(props, "ui"),
    config,
    toRef(props, "class"),
);

function onClick(
    e: Event,
    item: ExtendedDropdownItem,
    {
        href,
        navigate,
        isExternal,
    }: {
        href: boolean;
        navigate: (e: Event) => void;
        isExternal: boolean;
    },
) {
    if (item.click) {
        item.click(e);
    }
    if (href && !isExternal) {
        navigate(e);
    }

    emit("close", item);
}
</script>

<template>
    <div
        :class="[
            ui.base,
            ui.divide,
            ui.rounded,
            ui.shadow,
            ui.background,
            ui.height,
        ]"
    >
        <div
            v-for="(subItems, index) of items"
            :key="index"
            :class="ui.padding"
        >
            <NuxtLink
                v-for="(item, subIndex) of subItems"
                :key="subIndex"
                v-slot="{ href, target, rel, navigate, isExternal, isActive }"
                v-bind="getNuxtLinkProps(item)"
                custom
            >
                <component
                    :is="!!href ? 'a' : 'button'"
                    :href="!item.disabled ? href : undefined"
                    :rel="rel"
                    :target="target"
                    :class="
                        twMerge(
                            twJoin(
                                ui.item.base,
                                ui.item.padding,
                                ui.item.size,
                                ui.item.rounded,
                                ...(item.active || isActive
                                    ? [
                                          item.active && ui.item.selected
                                              ? ui.item.selected
                                              : ui.item.active,
                                      ]
                                    : [
                                          ui.item.active
                                              .split(' ')
                                              .map(
                                                  (style) =>
                                                      `hover:${style} active:${style}`,
                                              )
                                              .join(' '),
                                          ui.item.inactive,
                                      ]),
                                item.disabled && ui.item.disabled,
                            ),
                            item.class,
                        )
                    "
                    @click="
                        onClick($event, item, {
                            href,
                            navigate,
                            isExternal,
                        })
                    "
                >
                    <slot :name="item.slot || 'item'" :item="item">
                        <FontAwesomeIcon
                            v-if="item.icon"
                            :icon="item.icon"
                            :class="
                                twMerge(
                                    twJoin(
                                        ui.item.icon.base,
                                        ui.item.active
                                            .split(' ')
                                            .map(
                                                (style) =>
                                                    `hover:${style} active:${style}`,
                                            )
                                            .join(' '),
                                        item.active
                                            ? ui.item.icon.active
                                            : ui.item.icon.inactive,
                                    ),
                                    item.iconClass,
                                )
                            "
                        />
                        <UAvatar
                            v-else-if="item.avatar"
                            v-bind="{
                                size: ui.item.avatar.size,
                                ...item.avatar,
                            }"
                            :class="ui.item.avatar.base"
                        />

                        <span
                            :class="
                                twMerge(
                                    ui.item.label,
                                    item.labelClass,
                                    item.badge && 'flex mr-2',
                                )
                            "
                        >
                            <span class="truncate">{{ item.label }}</span>
                            <UBadge
                                v-if="item.badge"
                                class="font-medium ml-2"
                                color="gray"
                                size="sm"
                                :label="item.badge"
                                :ui="{ rounded: 'rounded-full' }"
                            />
                        </span>

                        <span
                            v-if="item.shortcuts?.length"
                            :class="ui.item.shortcuts"
                        >
                            <UKbd
                                v-for="shortcut of item.shortcuts"
                                :key="shortcut"
                                >{{ shortcut }}</UKbd
                            >
                        </span>
                        <FontAwesomeIcon
                            v-if="item.trailingIcon"
                            :icon="item.trailingIcon"
                            :class="
                                twMerge(
                                    twJoin(
                                        ui.item.icon.base,
                                        ui.item.active
                                            .split(' ')
                                            .map(
                                                (style) =>
                                                    `hover:${style} active:${style}`,
                                            )
                                            .join(' '),
                                        item.active
                                            ? ui.item.icon.active
                                            : ui.item.icon.inactive,
                                    ),
                                    item.iconClass,
                                )
                            "
                        />
                    </slot>
                </component>
            </NuxtLink>
        </div>
    </div>
</template>
